/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

// React
import React from "react";
// redux hooks
import { useFieldArray, Controller } from "react-hook-form";
// BASE components
import BASESvgIcon from '../../../../../../../@BASE/core/BASESvgIcon';
// material-ui components
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from "@mui/system/Box";
import Button from "@mui/material/Button";
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import IconButton from "@mui/material/IconButton";

import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import TextField from "@mui/material/TextField";
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Typography from "@mui/material/Typography";
// custom components
import NumberedHeading from "../../../../../../shared-components/forms/NumberedHeading";
import LinguisticEvaluationLureArray from "./LinguisticEvaluationLureArray";
import StatementMaterialsArray from "../../shared/StatementMaterialsArray";

export default function LinguisticEvaluationArray({ control, register, setValue, getValues, errors, inputDir }) {
  // setup field array
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'content.statements',
  });

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0,
          },
        }}
        className="px-0"
      >
        {fields.map((item, index) => {
          return (
            <TimelineItem key={item.id}>
              <TimelineSeparator>
                <TimelineDot color="secondary" sx={{marginTop: '22px'}} />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent className="py-0">
                <Accordion
                  defaultExpanded
                  className="bg-transparent shadow-none"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${index}`}
                    id={`${index}`}
                    className="py-0 px-16 m-0 rounded bg-white shadow-sm"
                    sx={{
                      border: 'solid 1px',
                      borderColor: '#bdbdbd'
                    }}
                  >
                    <Typography className="grow m-0 font-semibold">
                      Statement {index + 1}
                    </Typography>
                    {index !== 0 &&
                      <IconButton 
                        className="ml-16 mr-8"
                        aria-label="move up"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index - 1);
                        }}
                      >
                        <BASESvgIcon size={20}>heroicons-solid:arrow-circle-up</BASESvgIcon>
                      </IconButton>
                    }
                    {index !== fields.length - 1 &&
                      <IconButton 
                        className="mx-8"
                        aria-label="move down"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index + 1);
                        }}
                      >
                        <BASESvgIcon size={20}>heroicons-solid:arrow-circle-down</BASESvgIcon>
                      </IconButton>
                    }
                    <IconButton
                      className="ml-8 mr-16"
                      aria-label="delete"
                      color="error"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(index);
                      }}
                    >
                      <BASESvgIcon size={20}>heroicons-outline:trash</BASESvgIcon>
                    </IconButton>
                  </AccordionSummary>
                  <AccordionDetails className="p-0">
                    <Box
                      className="p-0"
                    >
                      <NumberedHeading number="1" title="Materials" instructions='Using the controls below, please add materials for the statement.' />
                      <Controller
                        control={control}
                        name={`content.statements.${index}.materials`}
                        render={({ field }) => <StatementMaterialsArray nestIndex={index} inputDir={inputDir} {...{ control, errors }} />}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading number="2" title="Prompt" instructions='Enter the statement in the target language.' />
                      <Controller
                        control={control}
                        name={`content.statements.${index}.prompt`}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              className="mt-16 mb-16"
                              label="Text"
                              placeholder="Text"
                              error={!!errors.content?.statements?.[index]?.prompt}
                              helperText={errors.content?.statements?.[index]?.prompt?.message}
                              fullWidth
                              multiline
                              minRows={5}
                              maxRows={10}
                              required
                              dir={inputDir}
                            />
                          </>
                        )}
                      />

                      <Divider className="mt-16" />

                      <Grid container spacing={2} className="mb-32">
                        <Grid item xs={12} sm={6} md={4}>
                          <NumberedHeading number="3" title="Option 1" instructions='Enter a name for option 1 (Yes)' />
                          <Controller
                            control={control}
                            name={`content.statements.${index}.option_1`}
                            render={({ field }) => (
                              <>
                                <TextField
                                  {...field}
                                  className="mt-16 mb-16"
                                  label="Option 1"
                                  placeholder="Option 1"
                                  error={!!errors.content?.statements?.[index]?.option_1}
                                  helperText={errors.content?.statements?.[index]?.option_1?.message}
                                  fullWidth
                                  required
                                />
                              </>
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                        <NumberedHeading number="4" title="Option 2" instructions='Enter a name for option 2 (No)' />
                          <Controller
                            control={control}
                            name={`content.statements.${index}.option_2`}
                            render={({ field }) => (
                              <>
                                <TextField
                                  {...field}
                                  className="mt-16 mb-16"
                                  label="Option 2"
                                  placeholder="Option 2"
                                  error={!!errors.content?.statements?.[index]?.option_2}
                                  helperText={errors.content?.statements?.[index]?.option_2?.message}
                                  fullWidth
                                  required
                                />
                              </>
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <NumberedHeading number="5" title="Option 3" instructions='Enter a name for option 3 (N/A)' />
                          <Controller
                            control={control}
                            name={`content.statements.${index}.option_3`}
                            render={({ field }) => (
                              <>
                                <TextField
                                  {...field}
                                  className="mt-16 mb-16"
                                  label="Option 3"
                                  placeholder="Option 3"
                                  error={!!errors.content?.statements?.[index]?.option_3}
                                  helperText={errors.content?.statements?.[index]?.option_3?.message}
                                  fullWidth
                                  required
                                />
                              </>
                            )}
                          />
                        </Grid>
                      </Grid>

                      <Divider className="mt-16" />

                      <NumberedHeading number="6" title="Lures" instructions='Using the controls below, please add lures for the question.' />
                      <Controller
                        control={control}
                        name={`content.statements.${index}.lures`}
                        render={({ field }) => <LinguisticEvaluationLureArray nestIndex={index} inputDir={inputDir} {...{ control, errors }} />}
                      />
                
                      <Divider className="mt-16" />
                
                      <NumberedHeading number="7" title="First Incorrect Feedback" instructions="Enter the feedback for the first incorrect response." />
                      <Controller
                        control={control}
                        name={`content.statements.${index}.incorrect_feedback_1`}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              className="mt-16 mb-16"
                              label="First Incorrect Feedback"
                              placeholder="First Incorrect Feedback"
                              variant="outlined"
                              fullWidth
                              error={!!errors.content?.statements?.[index]?.incorrect_feedback_1}
                              helperText={errors.content?.statements?.[index]?.incorrect_feedback_1?.message}
                              required
                              multiline
                              minRows={1}
                              maxRows={10}
                              dir={inputDir}
                            />
                          </>
                        )}
                      />
                
                      <Divider className="mt-16" />
                
                      <NumberedHeading number="8" title="Second Incorrect Feedback" instructions="Enter the feedback for the second incorrect response." />
                      <Controller
                        control={control}
                        name={`content.statements.${index}.incorrect_feedback_2`}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              className="mt-16 mb-16"
                              label="Second Incorrect Feedback"
                              placeholder="Second Incorrect Feedback"
                              variant="outlined"
                              fullWidth
                              error={!!errors.content?.statements?.[index]?.incorrect_feedback_2}
                              helperText={errors.content?.statements?.[index]?.incorrect_feedback_2?.message}
                              required
                              multiline
                              minRows={1}
                              maxRows={10}
                              dir={inputDir}
                            />
                          </>
                        )}
                      />
                      <Divider className="mt-16" />
                      
                    </Box>
                  </AccordionDetails>
                  <AccordionActions>
                    {index !== 0 &&
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index - 1)}
                        startIcon={<BASESvgIcon size={20}>heroicons-solid:arrow-circle-up</BASESvgIcon>}
                        size="small"
                      >
                        Move Up
                      </Button>
                    }
                    {index !== fields.length - 1 &&
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index + 1)}
                        startIcon={<BASESvgIcon size={20}>heroicons-solid:arrow-circle-down</BASESvgIcon>}
                        size="small"
                      >
                        Move Down
                      </Button>
                    }
                    <Button
                      variant="contained"
                      onClick={() => remove(index)}
                      color="error"
                      startIcon={<BASESvgIcon size={20}>heroicons-outline:trash</BASESvgIcon>}
                      size="small"
                    >
                      Remove
                    </Button>
                  </AccordionActions>
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
      <Box className="flex items-center mt-0 p-0">
        <Button
          className="group flex items-center mt-8 rounded cursor-pointer"
          color="secondary"
          variant="contained"
          onClick={() => append({ 
            materials: [
              {
                material_title_en: "",
                material_title_tl: "",  
                link: "",
                media_material: false,
                sp_url: "",
                media_page_id: "",
              },
            ],
            prompt: "",
            option_1: "",
            option_2: "",
            option_3: "",
            lures: [
              {
                lure_text: "",
                correct_option: 1,
                lure_correct_feedback: "",
                lure_incorrect_feedback: "",
              },
            ],
            incorrect_feedback_1: "",
            incorrect_feedback_2: "",
          })}
        >
          <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
          <span className="ml-8 font-medium text-secondary group-hover:underline">
            Add Statement
          </span>
        </Button>
      </Box>
    </>
  );
}
